import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "./store";

export interface StructuredResponse {
  headline: string;
  test_design: string | null;
  hypothesis: string | null;
  how_it_works: string | null;
  kpis?: string[];
  measurement_method: string | null;
  minimum_spend: string | null;
  duration: string | null;
  key_insight: string | null;
  next_steps: string | null;
}

export interface MessageItem {
  role: "user" | "assistant";
  content: string;
  image_url: string | null;
  design: string | null;
  confidence: "high" | "medium" | "low" | "general" | null;
  reason: string | null;
  follow_up: string | null;
  structured: StructuredResponse | null;
  timestamp: string;
}

interface TestDesignState {
  messages: MessageItem[];
  isLoading: boolean;
  matchedDesign: object | null;
  hasMatched: boolean;
  error: string | null;
  sessionLoading: boolean;
  sessionFetched: boolean;
  sessionExists: boolean;
}

const initialState: TestDesignState = {
  messages: [],
  isLoading: false,
  matchedDesign: null,
  hasMatched: false,
  error: null,
  sessionLoading: true,
  sessionFetched: false,
  sessionExists: false,
};

const testDesignSlice = createSlice({
  name: "testDesign",
  initialState,
  reducers: {
    setMessages(state, action: PayloadAction<MessageItem[]>) {
      state.messages = action.payload;
    },
    addMessage(state, action: PayloadAction<MessageItem>) {
      state.messages.push(action.payload);
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
    setMatchedDesign(state, action: PayloadAction<object | null>) {
      state.matchedDesign = action.payload;
    },
    setHasMatched(state, action: PayloadAction<boolean>) {
      state.hasMatched = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
    clearSession(state) {
      state.messages = [];
      state.isLoading = false;
      state.matchedDesign = null;
      state.hasMatched = false;
      state.error = null;
      state.sessionLoading = false;
      state.sessionFetched = false;
      state.sessionExists = false;
    },
    setSessionLoading(state, action: PayloadAction<boolean>) {
      state.sessionLoading = action.payload;
    },
    setSessionFetched(state, action: PayloadAction<boolean>) {
      state.sessionFetched = action.payload;
    },
    setSessionExists(state, action: PayloadAction<boolean>) {
      state.sessionExists = action.payload;
    },
    resetForStudySwitch(state) {
      state.messages = [];
      state.hasMatched = false;
      state.sessionLoading = true;
      state.sessionFetched = false;
      state.sessionExists = false;
    },
  },
});

export const {
  setMessages,
  addMessage,
  setLoading,
  setMatchedDesign,
  setHasMatched,
  setError,
  clearSession,
  setSessionLoading,
  setSessionFetched,
  setSessionExists,
  resetForStudySwitch,
} = testDesignSlice.actions;

export const selectMessages = (state: RootState) => state.testDesign.messages;
export const selectIsLoading = (state: RootState) => state.testDesign.isLoading;
export const selectMatchedDesign = (state: RootState) => state.testDesign.matchedDesign;
export const selectHasMatched = (state: RootState) => state.testDesign.hasMatched;
export const selectError = (state: RootState) => state.testDesign.error;
export const selectSessionLoading = (state: RootState) => state.testDesign.sessionLoading;
export const selectSessionFetched = (state: RootState) => state.testDesign.sessionFetched;
export const selectSessionExists = (state: RootState) => state.testDesign.sessionExists;

export default testDesignSlice.reducer;
